import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AddContainer, Texts, Component4, Component3, Component2, InnerComponent, HeaderPart, FooterPart, SaveButton, CancelButton } from './Style.js';
import { useTheme } from '@emotion/react';
import InputComponent from 'src/components/shared/Form/Input/index.js';
import { mobileTypography, palette, shadows, typography } from 'src/config/theme.js';
import { alpha, MenuItem, Box, Typography } from '@mui/material';
import DateTimePicker from 'src/components/App/DatePicker/index.js';
import { ReactComponent as CloseIcon } from 'src/assets/images/close-circle.svg';
import { ReactComponent as AddIcon } from 'src/assets/images/add-square.svg';
import { ReactComponent as PersonIcon } from 'src/assets/images/profile-circle.svg';
import { postAppointmentRequest, getClinicProfileByIdRequest, getAvailableSlotsRequest, fetchAvailabilityRequest, postBillingRequest, getDoctorProfileByIdRequest, postMessageRequest, getPatientByIdRequest, getAppointmentByPatientIdRequest, postPatientInfoRequest, updatePatientByIdRequest } from 'src/modules/app/store/appActions.js';
import { postNotification, getActiveCase } from 'src/modules/app/api/appApi';
import { openWhatsApp } from 'src/utils/whatsapp';
import { playNotificationSound } from 'src/utils/notifySound';
import useResponsive from 'src/components/hooks/useResponsive.js';
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useParams } from 'react-router-dom';
import { getUserFromToken } from 'src/DecodeToken.js';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'src/utils/toast.js';
import useAppointmentDetails from '../../UseAppointmentDetails.js';
import storage from 'src/utils/storageUtils.js';
import InputSelect from 'src/components/shared/Form/Select/index.js';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ButtonComponent from 'src/components/shared/Button';

dayjs.extend(customParseFormat);

export function FetchSlotsEffect({ clinicId, token }) {
    const { values } = useFormikContext();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!values.date || !clinicId) return;

        const formattedDate = dayjs(values.date).format('YYYY-MM-DD');
        dispatch(getAvailableSlotsRequest(
            {
                token,
                params: {
                    clinicId,
                    date: formattedDate,
                },
            },
            null,
            (err) => {
                console.error('Failed to fetch available slots:', err);
            }
        ));
    }, [values.date, clinicId]);

    return null;
}


export function UpdateFeesEffect({ oldCaseFees, newCaseFees, gap, doctorTimings, patientId, doctorId, clinicId, activeCase, setActiveCase, clinicProfile }) {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
        const fetchAndCalculateFees = async () => {
            if (!patientId || !doctorId || !clinicId) return;

            try {
                const response = await getActiveCase({ patient: patientId, doctor: doctorId, clinic: clinicId });
                if (response && response.activeCase) {
                    setActiveCase(response.activeCase);
                    setFieldValue('consultingFees', oldCaseFees || 0);
                } else {
                    setActiveCase(null);
                    setFieldValue('consultingFees', newCaseFees || 0);
                }
            } catch (error) {
                console.error('Failed to fetch active case in UpdateFeesEffect:', error);
                setActiveCase(null);
                setFieldValue('consultingFees', newCaseFees || 0);
            }
        };

        fetchAndCalculateFees();
    }, [patientId, doctorId, clinicId, oldCaseFees, newCaseFees, setFieldValue, setActiveCase]);

    useEffect(() => {
        if (gap && gap > 0 && values.time) {
            if (!values.time_touched) {
                const currentGap = gap;
                const d = dayjs(values.date);
                const now = dayjs();
                const isToday = d.isSame(now, 'day');
                const selectedDay = d.format('dddd').toLowerCase();
                
                const associatedDoctors = clinicProfile?.clinicSetting?.associatedDoctors || [];
                const isSingleDoctor = associatedDoctors.length <= 1;

                const dayTimings = (doctorTimings?.timings || []).filter(t => (t.day?.value || t.day)?.toLowerCase() === selectedDay);
                const clinicTimings = (clinicProfile?.clinicTiming || []).filter(t => (t.day?.value || t.day)?.toLowerCase() === selectedDay);

                const ranges = isSingleDoctor 
                    ? (clinicTimings.length > 0 ? clinicTimings : [{ startTime: '08:00 AM', endTime: '09:00 PM' }])
                    : (dayTimings.length > 0 ? dayTimings : [{ startTime: '08:00 AM', endTime: '09:00 PM' }]);

                let startHour = 8;
                let startMinute = 0;

                if (ranges.length > 0) {
                    const firstTiming = ranges[0];
                    const startTimeStr = firstTiming.startTime;
                    const start = dayjs(startTimeStr).isValid() ? dayjs(startTimeStr) : dayjs(startTimeStr, ['h:mm A', 'hh:mm A', 'HH:mm', 'hA', 'ha']);
                    
                    if (start.isValid()) {
                        startHour = start.hour();
                        startMinute = start.minute();
                    }
                }

                let current = d.startOf('day').hour(startHour).minute(startMinute);
                if (isToday) {
                    const minPriorTime = clinicProfile?.clinicSetting?.minimumPriorBookingTime || 0;
                    const threshold = now.add(minPriorTime, 'minutes');
                    if (threshold.isAfter(current)) {
                        current = threshold;
                    }
                }
                
                const minutes = current.minute();
                const roundedMins = Math.ceil(minutes / currentGap) * currentGap;
                const newTime = current.minute(roundedMins).second(0).format('HH:mm');
                setFieldValue('time', newTime);
            }
        }
    }, [gap, doctorTimings, clinicProfile]);

    return null;
}



function AppointmentTabForm({ onClose, fetchAppointments }) {
    const { clinicId: urlClinicId, doctorId: urlDoctorId } = useParams();
    const { PatientId, user, clinicId: hookClinicId } = useAppointmentDetails();
    const clinicId = hookClinicId || urlClinicId;
    const token = storage.get('TOKEN');
    const { isMobile, isTablet } = useResponsive();
    const theme = useTheme();
    const dispatch = useDispatch();
    const availableSlots = useSelector(state => state.app.availableSlots || []);
    const patientById = useSelector(state => state.app.patientById);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);
    const [isClinicOpen, setIsClinicOpen] = useState(true);
    const [lastVisitDays, setLastVisitDays] = useState(null);
    const [activeCase, setActiveCase] = useState(null);
    const clinicData = useSelector((state) => state.app.clinics);
    const doctorProfile = useSelector((state) => state.app.getDoctProfileById);
    const userId = urlDoctorId || getUserFromToken();

    const [clinicProfile, setClinicProfile] = useState(null);

    useEffect(() => {
        if (userId && !doctorProfile) {
            dispatch(getDoctorProfileByIdRequest(userId));
        }
    }, [userId, dispatch, doctorProfile]);

    useEffect(() => {
        if (clinicId) {
            dispatch(getClinicProfileByIdRequest(clinicId, (res) => {
                setClinicProfile(res?.profile || res);
            }));
        }
    }, [clinicId, dispatch]);

    const doctorTimings = (doctorProfile?.data?.doctorTimings || doctorProfile?.doctorTimings || doctorProfile?.payload?.data?.doctorTimings) || {};
    const doctorConsulting = (doctorProfile?.data?.consulting || doctorProfile?.consulting || doctorProfile?.payload?.data?.consulting || doctorProfile) || {};
    
    const isDoctorLoaded = !!(doctorProfile);
    const isClinicLoaded = clinicId ? !!(clinicProfile) : true;
    const isProfilesLoaded = isDoctorLoaded && isClinicLoaded;

    const clinicHasSettings = clinicProfile?.clinicSetting && Object.keys(clinicProfile.clinicSetting).filter(k => k !== 'clinic').length > 0;
    const isAcceptingTokens = clinicProfile?.clinicSetting?.acceptingTokens === 'yes';
    
    const associatedDoctors = clinicProfile?.clinicSetting?.associatedDoctors || [];
    const isSingleDoctor = associatedDoctors.length <= 1;

    const consultingData = (isProfilesLoaded && clinicHasSettings)
        ? {
            newCaseConsultingFees: clinicProfile.clinicSetting.consultingFees > 0 ? clinicProfile.clinicSetting.consultingFees : (doctorConsulting.newCaseConsultingFees || 0),
            oldCaseConsultingFees: clinicProfile.clinicSetting.oldCaseConsultingFees > 0 ? clinicProfile.clinicSetting.oldCaseConsultingFees : (doctorConsulting.oldCaseConsultingFees || 0),
            consultationValidityDays: clinicProfile.clinicSetting.caseValidityDays || doctorConsulting.consultationValidityDays || 90,
            gapBetweenAppointments: isSingleDoctor 
                ? (clinicProfile.clinicSetting.gapBetweenAppointments || 15)
                : (doctorConsulting.gapBetweenAppointments || 15)
          }
        : doctorConsulting;

    let gap = Number(consultingData.gapBetweenAppointments || 0);
    
    // Fallback to check if gap is stored directly on the profile object
    if (gap <= 0 && doctorProfile?.gapBetweenAppointments) {
        gap = Number(doctorProfile.gapBetweenAppointments);
    }
    // Another fallback for nested structure
    if (gap <= 0 && doctorProfile?.data?.gapBetweenAppointments) {
        gap = Number(doctorProfile.data.gapBetweenAppointments);
    }
    
    const oldCaseFees = Number(consultingData.oldCaseConsultingFees || 0);
    const newCaseFees = Number(consultingData.newCaseConsultingFees || 0);
    const validityDays = Number(consultingData.consultationValidityDays || 90);
    console.log('Doctor Profile Data (Tab):', doctorProfile);
    console.log('Consulting Data (Tab):', consultingData);
    console.log('Calculated Gap (Tab):', gap);

    const isAccepting = (settings) => {
        const raw = settings?.acceptingAppointments ?? settings?.acceptingTokens;
        if (typeof raw === 'string') return raw.toLowerCase() === 'yes';
        if (typeof raw === 'boolean') return raw;
        return true;
    };

    console.log('available slots:', availableSlots)
    const validationSchema = Yup.object().shape({
        date: Yup.date().required('Select Appointment Date'),
        time: Yup.string().required('Select Appointment Time'),
        addnotes: Yup.string().required('Enter Symptoms'),
        consultingFees: Yup.number().min(0, 'Fees must be 0 or more').required('Consulting Fees is required'),
    });

    useEffect(() => {
        const id = clinicData?.currentClinic?.id || clinicId;
        if (!id) return;
        dispatch(fetchAvailabilityRequest(
            id,
            (res) => {
                setIsClinicOpen(isAccepting(res?.clinicSettings));
            },
            () => { }
        ));
    }, [clinicData?.currentClinic?.id, clinicId, dispatch]);

    const doSubmit = async (values, resetForm) => {
        const userId = urlDoctorId || getUserFromToken();
        if (!userId) {
            toast.error('Failed to identify user. Please login again.');
            return;
        }

        try {
            const appointmentData = {
                date: dayjs(values.date).format('YYYY-MM-DD'),
                time: values.time,
                addnotes: values.addnotes,
                scheduleType: values.scheduleType,
                recordStatus: values.scheduleType === 'token' ? 'In Queue' : 'Scheduled',
                user: userId,
                patient: PatientId,
                clinic: clinicId,
            };

            const response = await new Promise((resolve, reject) => {
                dispatch(postAppointmentRequest({ data: appointmentData, resolve, reject }));
            });

            // Save Patient Info (Vitals) if provided
            const vitalsData = {
                weight: values.weight,
                height: values.height,
                pulseRate: values.pulseRate,
                spo2: values.spo2,
                respiratoryRate: values.respiratoryRate,
                temperature: values.temperature,
                patient: PatientId,
                user: userId,
                clinic: clinicId,
            };

            // Check if at least one vital is provided
            const vitalFields = [values.weight, values.height, values.pulseRate, values.spo2, values.respiratoryRate, values.temperature];
            const hasVitals = vitalFields.some(v => v !== undefined && v !== null && v !== '');
            if (hasVitals) {
                try {
                    await new Promise((resolve, reject) => {
                        dispatch(postPatientInfoRequest(vitalsData, resolve, reject));
                    });
                    console.log('🟢 VITALS SAVED (Tab Form)');
                } catch (vitalsErr) {
                    console.error('🔴 VITALS SAVE FAILED (Tab Form):', vitalsErr);
                }
            }


            toast.success(response.message);

            // 3. TRIGGER WHATSAPP REDIRECT IMMEDIATELY AFTER APPOINTMENT SUCCESS
            try {
                const confirmationText = `Your appointment is scheduled on ${dayjs(values.date).format('DD-MM-YYYY')} at ${values.time}.`;
                
                let phone = patientById?.data?.contactNo;
                if (!phone) {
                    // If not in Redux, fetch patient details manually
                    const patientDetails = await new Promise((resolve, reject) => {
                        dispatch(getPatientByIdRequest(PatientId, resolve, reject));
                    });
                    phone = patientDetails?.patient?.contactNo;
                }

                console.log('Attempting WhatsApp redirect (Tab Form). Phone:', phone);
                
                if (phone) {
                    const msgPayload = {
                        language: 'english',
                        title: 'Appointment Confirmation',
                        message: confirmationText,
                        clinic: clinicId,
                        patient: PatientId,
                    };
                    dispatch(postMessageRequest(msgPayload, () => {}, () => {}));
                    
                    // openWhatsApp(phone, confirmationText);
                }
            } catch (err) {
                console.error('WhatsApp redirection error (Tab Form):', err);
            }

            const tokenApptId = response?.tokenappointment?.id;
            const feesAmount = Number(values.consultingFees || 0);
            if (tokenApptId && feesAmount > 0) {
                // 1. Create Debit entry (Total Charges)
                const debitPayload = {
                    paymentFor: 'consultation',
                    amount: feesAmount,
                    date: dayjs().format('YYYY-MM-DD'),
                    type: 'debit',
                    method: 'cash',
                    clinic: clinicId,
                    patient: PatientId,
                    user,
                    tokenAppointment: tokenApptId,
                };

                // 2. Create Credit entry (Total Paid)
                const creditPayload = {
                    paymentFor: 'consultation',
                    amount: feesAmount,
                    date: dayjs().format('YYYY-MM-DD'),
                    type: 'credit',
                    method: 'cash',
                    clinic: clinicId,
                    patient: PatientId,
                    user,
                    tokenAppointment: tokenApptId,
                };

                try {
                    // Sequential billing: takeLatest cancels first request in Promise.all
                    await new Promise((resolve, reject) => {
                        dispatch(postBillingRequest(debitPayload, resolve, reject));
                    });
                    await new Promise((resolve, reject) => {
                        dispatch(postBillingRequest(creditPayload, resolve, reject));
                    });
                    window.dispatchEvent(new CustomEvent('app:billingCreated'));
                } catch (_) {}
            }

            try {
                const notifPayload = {
                    message: `New appointment booked on ${dayjs(values.date).format('DD-MM-YYYY')} at ${values.time}.`,
                    url: `${window.location.origin}/appointment/Patients/${PatientId}`,
                    user: user
                };
                await postNotification(notifPayload);
                window.dispatchEvent(new CustomEvent('app:notificationCreated'));
            } catch (_) {}
            try { playNotificationSound(); } catch (_) {}

            if (response?.tokenappointment?.id && fetchAppointments) {
                fetchAppointments();
            }


            onClose();
            resetForm();
        } catch (error) {
            toast.error(error.message || 'Failed to add appointment');
        }
    };

    const handleSubmit = async (values, { resetForm }) => {
        try {
            const res = await new Promise((resolve, reject) => {
                dispatch(fetchAvailabilityRequest(
                    clinicId,
                    resolve,
                    reject
                ));
            });
            if (!isAccepting(res?.clinicSettings)) {
                setPendingValues({ values, resetForm });
                setConfirmOpen(true);
                return;
            }
        } catch (e) {
            if (!isClinicOpen) {
                setPendingValues({ values, resetForm });
                setConfirmOpen(true);
                return;
            }
        }
        await doSubmit(values, resetForm);
    };

    return (
        <Formik
            initialValues={{
                date: dayjs(),
                time: (function() {
                    let g = Number(consultingData.gapBetweenAppointments || 0);
                    if (g <= 0) return ''; // Don't set a default time if no gap is configured
                    let t = dayjs().add(g, 'minutes');
                    const mins = t.minute();
                    const roundedMins = Math.ceil(mins / g) * g;
                    return t.minute(roundedMins).second(0).format('HH:mm');
                })(),
                addnotes: '',
                consultingFees: oldCaseFees,
                weight: '',
                height: '',
                pulseRate: '',
                spo2: '',
                respiratoryRate: '',
                temperature: '',
                time_touched: false,
                scheduleType: 'appointment',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
                    <Form>
                        <FetchSlotsEffect clinicId={clinicId} token={token} />
                        <UpdateFeesEffect 
                            oldCaseFees={oldCaseFees} 
                            newCaseFees={newCaseFees}
                            gap={gap} 
                            doctorTimings={doctorTimings} 
                            patientId={PatientId}
                            doctorId={userId}
                            clinicId={clinicId}
                            activeCase={activeCase}
                            setActiveCase={setActiveCase}
                            clinicProfile={clinicProfile}
                        />
                        <HeaderPart>
                            Add Appointment
                            <CloseIcon width={isMobile || isTablet ? 18 : 24} height={isMobile || isTablet ? 18 : 24} onClick={onClose} style={{ cursor: 'pointer' }} />
                        </HeaderPart>

                    <AddContainer>
                        <Box sx={{ mb: 2 }}>
                            {activeCase ? (
                                <Box sx={{ 
                                    p: 1.5, 
                                    borderRadius: '12px', 
                                    backgroundColor: 'rgba(76, 175, 80, 0.08)', 
                                    border: '1px solid rgba(76, 175, 80, 0.3)',
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px' 
                                }}>
                                    <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#4CAF50' }} />
                                    <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                                        Active Case: {activeCase.caseNumber} (Valid till: {dayjs(activeCase.validityEndDate).format('DD-MM-YYYY')})
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ 
                                    p: 1.5, 
                                    borderRadius: '12px', 
                                    backgroundColor: 'rgba(244, 67, 54, 0.08)', 
                                    border: '1px solid rgba(244, 67, 54, 0.3)',
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px' 
                                }}>
                                    <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#F44336' }} />
                                    <Typography variant="body2" sx={{ color: '#F44336', fontWeight: 600 }}>
                                        No Active Case (Next visit will be New Case)
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Component4 sx={{ mb: 2 }}>
                            <Texts>Past Visit Days</Texts>
                            <InputComponent
                                name="pastVisitDays"
                                readOnly={true}
                                variant="outlined"
                                fullWidth
                                value={lastVisitDays !== null ? `${lastVisitDays} Days` : 'No past visits found'}
                                sx={{
                                    backgroundColor: alpha(palette.gery.black, 0.04),
                                    boxShadow: shadows[1],
                                    '& .MuiOutlinedInput-root': {
                                        padding: '10px 18px',
                                        borderRadius: '12px',
                                        height: '52px',
                                    },
                                    "& .MuiInputBase-input.MuiOutlinedInput-input": {
                                        ...(isMobile || isTablet ? (theme?.mobileTypography?.body2 || mobileTypography.body2) : (theme?.typography?.h5 || typography.h5)),
                                        color: alpha(palette.gery.black, 0.6),
                                    },
                                }}
                            />
                        </Component4>
                        <Component3>
                            <Texts>Appointment Date</Texts>
                            <DateTimePicker
                                name="date"
                                value={values.date}
                                onChange={(value) => setFieldValue('date', value)}
                                onBlur={handleBlur}
                                error={touched.date && Boolean(errors.date)}
                                helperText={touched.date && errors.date}
                                minDate={dayjs().startOf('day')}
                                type="date"
                                sx={{ height: "52px", borderRadius: '12px' }}
                            />
                        </Component3>

                        {isAcceptingTokens && (
                            <Component3>
                                <Texts>Schedule Type</Texts>
                                <InputSelect
                                    name="scheduleType"
                                    variant="outlined"
                                    showNone={false}
                                    value={values.scheduleType}
                                    onChange={(val) => setFieldValue('scheduleType', val)}
                                    onBlur={handleBlur}
                                    options={[
                                        { label: 'Appointment', value: 'appointment' },
                                        { label: 'Token', value: 'token' }
                                    ]}
                                    sx={{ height: "52px", borderRadius: '12px' }}
                                />
                            </Component3>
                        )}

                        <Component3>
                            <Texts>Appointment Timeslot</Texts>
                            <InputSelect
                                name="time"
                                variant="outlined"
                                showNone={false}
                                value={values.time}
                                onChange={(val) => {
                                        setFieldValue('time', val);
                                        setFieldValue('time_touched', true);
                                    }}
                                onBlur={handleBlur}
                                error={touched.time && Boolean(errors.time)}
                                helperText={touched.time && errors.time}
                                fullWidth
                                placeholder="Select Timeslot"
                                options={(function () {
                                        const is24 = (Intl.DateTimeFormat(undefined).resolvedOptions().hourCycle || '').includes('24');
                                        const d = dayjs(values.date).isValid() ? dayjs(values.date) : dayjs(values.date, 'DD/MM/YYYY');
                                        const now = dayjs();
                                        const isToday = d.format('YYYY-MM-DD') === now.format('YYYY-MM-DD');
                                        const selectedDay = d.format('dddd').toLowerCase();

                                        // Get all timings for the selected day
                                        const dayTimings = (doctorTimings?.timings || []).filter(t => (t.day?.value || t.day)?.toLowerCase() === selectedDay);
                                        const clinicTimings = (clinicProfile?.clinicTiming || []).filter(t => (t.day?.value || t.day)?.toLowerCase() === selectedDay);

                                        let currentGap = (gap && !isNaN(gap) && gap > 0) ? gap : 15;
                                        console.log('Generating slots with gap (Tab):', currentGap);

                                        const slots = [];
                                        const seenSlots = new Set();

                                        // If no timings set for this day, default to 8 AM to 9 PM
                                        const ranges = isSingleDoctor 
                                            ? (clinicTimings.length > 0 ? clinicTimings : [{ startTime: '08:00 AM', endTime: '09:00 PM' }])
                                            : (dayTimings.length > 0 ? dayTimings : [{ startTime: '08:00 AM', endTime: '09:00 PM' }]);

                                            const minPriorTime = Number(clinicProfile?.clinicSetting?.minimumPriorBookingTime || 0);
                                            const threshold = now.add(minPriorTime, 'minutes');
                                            
                                            console.log('[GraceTime Debug] TabForm - isToday:', isToday, 'GraceTime:', minPriorTime, 'Threshold:', threshold.format('hh:mm A'));

                                            ranges.forEach(timing => {
                                                const startTimeStr = timing.startTime;
                                                const endTimeStr = timing.endTime;

                                                const start = dayjs(startTimeStr).isValid() ? dayjs(startTimeStr) : dayjs(startTimeStr, ['h:mm A', 'hh:mm A', 'HH:mm', 'hA', 'ha']);
                                                const end = dayjs(endTimeStr).isValid() ? dayjs(endTimeStr) : dayjs(endTimeStr, ['h:mm A', 'hh:mm A', 'HH:mm', 'hA', 'ha']);
                                                
                                                if (!start.isValid() || !end.isValid()) return;

                                                let startHour = start.hour();
                                                let startMinute = start.minute();
                                                let endHour = end.hour();
                                                let endMinute = end.minute();

                                                let current = d.startOf('day').hour(startHour).minute(startMinute);
                                                
                                                const endOfRange = d.startOf('day').hour(endHour).minute(endMinute);

                                                for (let t = current; t.isBefore(endOfRange); t = t.add(currentGap, 'minutes')) {
                                                    const val = t.format('HH:mm');
                                                    const label = is24 ? t.format('HH:mm') : t.format('hh:mm A');

                                                    // Only show slot if it's NOT today, OR if it's after the grace time threshold
                                                    if (!isToday || t.isAfter(threshold)) {
                                                        if (!seenSlots.has(val)) {
                                                            slots.push({ label, value: val });
                                                            seenSlots.add(val);
                                                        }
                                                    }
                                                }
                                            });

                                        // Sort slots by time
                                        slots.sort((a, b) => a.value.localeCompare(b.value));

                                        return slots;
                                })()}
                                sx={{ height: "52px", borderRadius: '12px' }}
                            />
                        </Component3>

                        <Component4>
                            <Texts>Consulting Fees</Texts>
                            <InputComponent
                                name="consultingFees"
                                variant="outlined"
                                placeholder="Enter Amount"
                                fullWidth
                                value={values.consultingFees}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.consultingFees && Boolean(errors.consultingFees)}
                                helperText={touched.consultingFees && errors.consultingFees}
                                sx={{
                                    backgroundColor: palette.gery.white,
                                    boxShadow: shadows[1],
                                    '& .MuiOutlinedInput-root': {
                                        padding: '16px 18px',
                                        borderRadius: '12px',
                                        height: '52px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: `1px solid ${alpha(palette.gery.black, 0.14)}`,
                                    },
                                    "& .MuiInputBase-input.MuiOutlinedInput-input": {
                                        ...(isMobile || isTablet ? (theme?.mobileTypography?.body2 || mobileTypography.body2) : (theme?.typography?.h5 || typography.h5)),
                                    },
                                }}
                            />
                        </Component4>

                        <Component2>
                            <InnerComponent>
                                <Texts>Weight (kg)</Texts>
                                <InputComponent
                                    name="weight"
                                    variant="outlined"
                                    placeholder="kg"
                                    fullWidth
                                    value={values.weight}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    sx={{
                                        backgroundColor: palette.gery.white,
                                        boxShadow: shadows[1],
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            height: '52px',
                                        },
                                    }}
                                />
                            </InnerComponent>
                            <InnerComponent>
                                <Texts>Height (cm)</Texts>
                                <InputComponent
                                    name="height"
                                    variant="outlined"
                                    placeholder="cm"
                                    fullWidth
                                    value={values.height}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    sx={{
                                        backgroundColor: palette.gery.white,
                                        boxShadow: shadows[1],
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            height: '52px',
                                        },
                                    }}
                                />
                            </InnerComponent>
                        </Component2>

                        <Component2>
                            <InnerComponent>
                                <Texts>Pulse Rate (bpm)</Texts>
                                <InputComponent
                                    name="pulseRate"
                                    variant="outlined"
                                    placeholder="bpm"
                                    fullWidth
                                    value={values.pulseRate}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    sx={{
                                        backgroundColor: palette.gery.white,
                                        boxShadow: shadows[1],
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            height: '52px',
                                        },
                                    }}
                                />
                            </InnerComponent>
                            <InnerComponent>
                                <Texts>SpO2 (%)</Texts>
                                <InputComponent
                                    name="spo2"
                                    variant="outlined"
                                    placeholder="%"
                                    fullWidth
                                    value={values.spo2}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    sx={{
                                        backgroundColor: palette.gery.white,
                                        boxShadow: shadows[1],
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            height: '52px',
                                        },
                                    }}
                                />
                            </InnerComponent>
                        </Component2>

                        <Component2>
                            <InnerComponent>
                                <Texts>Resp. Rate (bpm)</Texts>
                                <InputComponent
                                    name="respiratoryRate"
                                    variant="outlined"
                                    placeholder="bpm"
                                    fullWidth
                                    value={values.respiratoryRate}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    sx={{
                                        backgroundColor: palette.gery.white,
                                        boxShadow: shadows[1],
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            height: '52px',
                                        },
                                    }}
                                />
                            </InnerComponent>
                            <InnerComponent>
                                <Texts>Temp (°F)</Texts>
                                <InputComponent
                                    name="temperature"
                                    variant="outlined"
                                    placeholder="°F"
                                    fullWidth
                                    value={values.temperature}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    sx={{
                                        backgroundColor: palette.gery.white,
                                        boxShadow: shadows[1],
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            height: '52px',
                                        },
                                    }}
                                />
                            </InnerComponent>
                        </Component2>


                        <Component4>
                            <Texts>Symptoms</Texts>
                            <InputComponent
                                name="addnotes"
                                variant="outlined"
                                fullWidth
                                multiline
                                value={values.addnotes}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.addnotes && Boolean(errors.addnotes)}
                                helperText={touched.addnotes && errors.addnotes}
                                rows={4}
                                sx={{
                                    backgroundColor: palette.gery.white,
                                    boxShadow: shadows[1],
                                    '& .MuiOutlinedInput-root': {
                                        padding: '16px 18px',
                                        borderRadius: '12px',
                                        height: '120px',
                                        alignItems: 'flex-start',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: `1px solid ${alpha(palette.gery.black, 0.14)}`,
                                    },
                                    "& .MuiInputBase-input.MuiOutlinedInput-input": {
                                        ...(isMobile || isTablet ? (theme?.mobileTypography?.body2 || mobileTypography.body2) : (theme?.typography?.h5 || typography.h5)),
                                    },
                                }}
                            />
                        </Component4>
                    </AddContainer>

                    <FooterPart>
                        <CancelButton
                            variant="none"
                            startIcon={<CloseIcon width={isMobile || isTablet ? 18 : 24} height={isMobile || isTablet ? 18 : 24} />}
                            onClick={onClose}
                        >
                            Cancel
                        </CancelButton>
                        <SaveButton
                            type="submit"
                            variant="outlined"
                            startIcon={<AddIcon width={isMobile || isTablet ? 18 : 24} height={isMobile || isTablet ? 18 : 24} />}
                            disabled={isSubmitting}
                        >
                            Save
                        </SaveButton>
                    </FooterPart>
                    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                        <DialogTitle>Clinic Closed</DialogTitle>
                        <DialogContent>Your clinic is closed. Are you sure you want to book appointment?</DialogContent>
                        <DialogActions>
                            <ButtonComponent
                                variant="outlined"
                                onClick={() => {
                                    setConfirmOpen(false);
                                    setPendingValues(null);
                                    onClose();
                                }}
                            >
                                No
                            </ButtonComponent>
                            <ButtonComponent
                                onClick={async () => {
                                    setConfirmOpen(false);
                                    const pv = pendingValues;
                                    if (pv) {
                                        await doSubmit(pv.values, pv.resetForm);
                                        setPendingValues(null);
                                    }
                                }}
                            >
                                Yes
                            </ButtonComponent>
                        </DialogActions>
                    </Dialog>
                </Form>
            )}
        </Formik>
    );
}

export default AppointmentTabForm;
